/**
 * Fall Event Overlay Component
 * 
 * Documentation for fall events in hospice care.
 * Includes fall circumstances, injuries, interventions, and follow-up care.
 */

import { useState, useEffect, useCallback } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Switch } from "../ui/switch";
import { ScrollArea } from "../ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { 
  AlertTriangle, Plus, History, Save, CheckCircle, Loader2,
  Clock, User, Activity, FileText, Shield, ChevronRight,
  XCircle, AlertCircle
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

// Fall circumstances options
const FALL_LOCATIONS = [
  { id: "bedroom", name: "Bedroom" },
  { id: "bathroom", name: "Bathroom" },
  { id: "living_room", name: "Living Room" },
  { id: "kitchen", name: "Kitchen" },
  { id: "hallway", name: "Hallway" },
  { id: "stairs", name: "Stairs" },
  { id: "outdoor", name: "Outdoor" },
  { id: "other", name: "Other" }
];

const FALL_ACTIVITIES = [
  { id: "transferring", name: "Transferring (bed/chair)" },
  { id: "ambulating", name: "Ambulating" },
  { id: "toileting", name: "Toileting" },
  { id: "bathing", name: "Bathing" },
  { id: "reaching", name: "Reaching/Bending" },
  { id: "standing", name: "Standing" },
  { id: "sleeping", name: "While sleeping/resting" },
  { id: "unknown", name: "Unknown/Found on floor" },
  { id: "other", name: "Other" }
];

const CONTRIBUTING_FACTORS = [
  { id: "weakness", name: "Generalized weakness" },
  { id: "dizziness", name: "Dizziness/Vertigo" },
  { id: "confusion", name: "Confusion/Cognitive impairment" },
  { id: "medication", name: "Medication effects" },
  { id: "vision", name: "Vision impairment" },
  { id: "environment", name: "Environmental hazard" },
  { id: "footwear", name: "Inappropriate footwear" },
  { id: "equipment", name: "Equipment failure" },
  { id: "hypotension", name: "Orthostatic hypotension" },
  { id: "pain", name: "Pain" },
  { id: "neuropathy", name: "Neuropathy" },
  { id: "unknown", name: "Unknown" }
];

const INJURY_TYPES = [
  { id: "none", name: "No visible injury" },
  { id: "skin_tear", name: "Skin tear" },
  { id: "bruise", name: "Bruise/Contusion" },
  { id: "laceration", name: "Laceration" },
  { id: "abrasion", name: "Abrasion" },
  { id: "swelling", name: "Swelling" },
  { id: "pain", name: "Pain/Tenderness" },
  { id: "fracture_suspected", name: "Suspected fracture" },
  { id: "fracture_confirmed", name: "Confirmed fracture" },
  { id: "head_injury", name: "Head injury" },
  { id: "other", name: "Other" }
];

const INJURY_LOCATIONS = [
  "Head", "Face", "Neck", "Shoulder", "Arm", "Elbow", "Wrist", "Hand",
  "Hip", "Thigh", "Knee", "Lower leg", "Ankle", "Foot", "Back", "Chest", "Abdomen"
];

const INTERVENTION_OPTIONS = [
  { id: "first_aid", name: "First aid provided" },
  { id: "ice_applied", name: "Ice applied" },
  { id: "wound_care", name: "Wound care" },
  { id: "vitals_monitored", name: "Vital signs monitored" },
  { id: "neuro_checks", name: "Neurological checks" },
  { id: "family_notified", name: "Family/Caregiver notified" },
  { id: "physician_notified", name: "Physician notified" },
  { id: "er_transfer", name: "ER transfer" },
  { id: "imaging_ordered", name: "X-ray/Imaging ordered" },
  { id: "medication_review", name: "Medication review" },
  { id: "fall_risk_updated", name: "Fall risk assessment updated" },
  { id: "environment_modified", name: "Environment modified" },
  { id: "equipment_ordered", name: "Equipment ordered" }
];

export function FallEventOverlay({ patientId, patientName, onSave, onClose }) {
  const [activeTab, setActiveTab] = useState("new");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fallHistory, setFallHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    // Event details
    fall_date: new Date().toISOString().split("T")[0],
    fall_time: new Date().toTimeString().slice(0, 5),
    witnessed: false,
    witness_name: "",
    
    // Circumstances
    location: "",
    location_other: "",
    activity: "",
    activity_other: "",
    contributing_factors: [],
    factor_other: "",
    
    // Pre-fall status
    pre_fall_mobility: "",
    assistive_device: "",
    fall_risk_score: "",
    
    // Injuries
    injuries_present: false,
    injury_types: [],
    injury_locations: [],
    injury_description: "",
    
    // Assessment post-fall
    vitals_checked: true,
    vitals_stable: true,
    vitals_notes: "",
    neuro_status: "normal",
    neuro_notes: "",
    able_to_ambulate: true,
    pain_level: 0,
    pain_location: "",
    
    // Interventions
    interventions: [],
    physician_name: "",
    physician_notification_time: "",
    family_name: "",
    family_notification_time: "",
    er_transferred: false,
    er_transfer_reason: "",
    
    // Follow-up
    follow_up_plan: "",
    fall_prevention_measures: "",
    equipment_recommendations: "",
    
    // Notes
    description: "",
    notes: ""
  });

  // Load fall history
  const loadFallHistory = useCallback(async () => {
    if (!patientId) return;
    setLoadingHistory(true);
    try {
      const response = await api.get(`/clinical/fall-events/${patientId}`);
      setFallHistory(response.data.events || []);
    } catch (err) {
      // Demo data fallback
      setFallHistory([
        {
          id: "demo-1",
          fall_date: "2025-01-15",
          fall_time: "14:30",
          location: "bathroom",
          witnessed: true,
          injuries_present: false,
          interventions: ["vitals_monitored", "family_notified"],
          created_at: "2025-01-15T14:45:00Z"
        }
      ]);
    } finally {
      setLoadingHistory(false);
    }
  }, [patientId]);

  useEffect(() => {
    if (activeTab === "history") {
      loadFallHistory();
    }
  }, [activeTab, loadFallHistory]);

  // Update form field
  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Toggle array field
  const toggleArrayField = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(v => v !== value)
        : [...prev[field], value]
    }));
  };

  // Handle save
  const handleSave = async () => {
    // Validation
    if (!formData.fall_date) {
      toast.error("Fall date is required");
      return;
    }
    if (!formData.location) {
      toast.error("Fall location is required");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        patient_id: patientId,
        ...formData
      };

      const response = await api.post("/clinical/fall-events", payload);
      toast.success("Fall event documented successfully");
      
      if (onSave) onSave(response.data);
      
      // Reset form
      setFormData({
        fall_date: new Date().toISOString().split("T")[0],
        fall_time: new Date().toTimeString().slice(0, 5),
        witnessed: false,
        witness_name: "",
        location: "",
        location_other: "",
        activity: "",
        activity_other: "",
        contributing_factors: [],
        factor_other: "",
        pre_fall_mobility: "",
        assistive_device: "",
        fall_risk_score: "",
        injuries_present: false,
        injury_types: [],
        injury_locations: [],
        injury_description: "",
        vitals_checked: true,
        vitals_stable: true,
        vitals_notes: "",
        neuro_status: "normal",
        neuro_notes: "",
        able_to_ambulate: true,
        pain_level: 0,
        pain_location: "",
        interventions: [],
        physician_name: "",
        physician_notification_time: "",
        family_name: "",
        family_notification_time: "",
        er_transferred: false,
        er_transfer_reason: "",
        follow_up_plan: "",
        fall_prevention_measures: "",
        equipment_recommendations: "",
        description: "",
        notes: ""
      });
      
      loadFallHistory();
    } catch (err) {
      toast.error("Failed to save fall event");
    } finally {
      setSaving(false);
    }
  };

  const getSeverityColor = (event) => {
    if (event.er_transferred) return "bg-red-500/20 text-red-400 border-red-500/50";
    if (event.injuries_present) return "bg-amber-500/20 text-amber-400 border-amber-500/50";
    return "bg-emerald-500/20 text-emerald-400 border-emerald-500/50";
  };

  return (
    <div className="space-y-4 p-4" data-testid="fall-event-overlay">
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <AlertTriangle className="w-5 h-5 text-amber-400" />
        <h3 className="font-semibold text-white">Fall Event Documentation</h3>
        <Badge variant="outline" className="border-slate-600 text-slate-300 ml-auto">{patientName}</Badge>
      </div>

      <div className="p-3 bg-amber-900/20 border border-amber-500/30 rounded-lg">
        <p className="text-sm text-amber-300">
          Document all fall events per CMS hospice guidelines. Include circumstances, injuries, interventions, and follow-up plan.
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2 bg-slate-800">
          <TabsTrigger value="new" className="flex items-center gap-2 data-[state=active]:bg-amber-600">
            <Plus className="w-4 h-4" />
            New Event
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-2 data-[state=active]:bg-slate-600">
            <History className="w-4 h-4" />
            History
          </TabsTrigger>
        </TabsList>

        {/* New Event Tab */}
        <TabsContent value="new" className="mt-4">
          <ScrollArea className="h-[600px] pr-4">
            <div className="space-y-4">
              {/* Event Details */}
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm text-white flex items-center gap-2">
                    <Clock className="w-4 h-4 text-amber-400" />
                    Event Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label className="text-slate-300">Fall Date *</Label>
                      <Input 
                        type="date" 
                        value={formData.fall_date} 
                        onChange={e => updateField("fall_date", e.target.value)}
                        className="bg-slate-800 border-slate-600 text-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-300">Fall Time</Label>
                      <Input 
                        type="time" 
                        value={formData.fall_time} 
                        onChange={e => updateField("fall_time", e.target.value)}
                        className="bg-slate-800 border-slate-600 text-white"
                      />
                    </div>
                    <div className="space-y-2 flex items-end gap-3">
                      <div className="flex items-center gap-2">
                        <Switch 
                          checked={formData.witnessed} 
                          onCheckedChange={c => updateField("witnessed", c)} 
                        />
                        <Label className="text-slate-300">Witnessed</Label>
                      </div>
                    </div>
                  </div>
                  
                  {formData.witnessed && (
                    <div className="space-y-2">
                      <Label className="text-slate-300">Witness Name</Label>
                      <Input 
                        value={formData.witness_name} 
                        onChange={e => updateField("witness_name", e.target.value)}
                        placeholder="Name of witness"
                        className="bg-slate-800 border-slate-600 text-white placeholder:text-slate-500"
                      />
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Circumstances */}
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm text-white">Fall Circumstances</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-slate-300">Location *</Label>
                      <Select value={formData.location} onValueChange={v => updateField("location", v)}>
                        <SelectTrigger className="bg-slate-800 border-slate-600 text-white">
                          <SelectValue placeholder="Select location" />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-800 border-slate-600">
                          {FALL_LOCATIONS.map(loc => (
                            <SelectItem key={loc.id} value={loc.id} className="text-white">{loc.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-300">Activity at Time of Fall</Label>
                      <Select value={formData.activity} onValueChange={v => updateField("activity", v)}>
                        <SelectTrigger className="bg-slate-800 border-slate-600 text-white">
                          <SelectValue placeholder="Select activity" />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-800 border-slate-600">
                          {FALL_ACTIVITIES.map(act => (
                            <SelectItem key={act.id} value={act.id} className="text-white">{act.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-slate-300">Contributing Factors</Label>
                    <div className="flex flex-wrap gap-2">
                      {CONTRIBUTING_FACTORS.map(factor => (
                        <Badge
                          key={factor.id}
                          variant={formData.contributing_factors.includes(factor.id) ? "default" : "outline"}
                          className={`cursor-pointer ${formData.contributing_factors.includes(factor.id) ? "bg-amber-600" : "border-slate-600 text-slate-300 hover:bg-slate-700"}`}
                          onClick={() => toggleArrayField("contributing_factors", factor.id)}
                        >
                          {factor.name}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Injuries */}
              <Card className={`border-slate-700 ${formData.injuries_present ? "bg-red-900/20 border-red-500/30" : "bg-slate-800/50"}`}>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm text-white flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-red-400" />
                      Injury Assessment
                    </span>
                    <div className="flex items-center gap-2">
                      <Switch 
                        checked={formData.injuries_present} 
                        onCheckedChange={c => updateField("injuries_present", c)} 
                      />
                      <Label className={formData.injuries_present ? "text-red-400" : "text-slate-400"}>
                        Injuries Present
                      </Label>
                    </div>
                  </CardTitle>
                </CardHeader>
                {formData.injuries_present && (
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-slate-300">Injury Types</Label>
                      <div className="flex flex-wrap gap-2">
                        {INJURY_TYPES.filter(t => t.id !== "none").map(type => (
                          <Badge
                            key={type.id}
                            variant={formData.injury_types.includes(type.id) ? "destructive" : "outline"}
                            className="cursor-pointer"
                            onClick={() => toggleArrayField("injury_types", type.id)}
                          >
                            {type.name}
                          </Badge>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-slate-300">Injury Locations</Label>
                      <div className="flex flex-wrap gap-2">
                        {INJURY_LOCATIONS.map(loc => (
                          <Badge
                            key={loc}
                            variant={formData.injury_locations.includes(loc) ? "default" : "outline"}
                            className={`cursor-pointer ${formData.injury_locations.includes(loc) ? "bg-red-600" : "border-slate-600 text-slate-300"}`}
                            onClick={() => toggleArrayField("injury_locations", loc)}
                          >
                            {loc}
                          </Badge>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-slate-300">Injury Description</Label>
                      <Textarea 
                        value={formData.injury_description} 
                        onChange={e => updateField("injury_description", e.target.value)}
                        placeholder="Describe injuries in detail..."
                        rows={3}
                        className="bg-slate-800 border-slate-600 text-white placeholder:text-slate-500"
                      />
                    </div>
                  </CardContent>
                )}
              </Card>

              {/* Post-Fall Assessment */}
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm text-white flex items-center gap-2">
                    <Activity className="w-4 h-4 text-blue-400" />
                    Post-Fall Assessment
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center gap-3">
                      <Switch 
                        checked={formData.vitals_stable} 
                        onCheckedChange={c => updateField("vitals_stable", c)} 
                      />
                      <Label className="text-slate-300">Vital Signs Stable</Label>
                    </div>
                    <div className="flex items-center gap-3">
                      <Switch 
                        checked={formData.able_to_ambulate} 
                        onCheckedChange={c => updateField("able_to_ambulate", c)} 
                      />
                      <Label className="text-slate-300">Able to Ambulate After Fall</Label>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-slate-300">Neurological Status</Label>
                      <Select value={formData.neuro_status} onValueChange={v => updateField("neuro_status", v)}>
                        <SelectTrigger className="bg-slate-800 border-slate-600 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-800 border-slate-600">
                          <SelectItem value="normal" className="text-white">Normal/Baseline</SelectItem>
                          <SelectItem value="altered" className="text-white">Altered from baseline</SelectItem>
                          <SelectItem value="loc" className="text-white">Loss of consciousness</SelectItem>
                          <SelectItem value="confusion" className="text-white">New confusion</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-300">Pain Level (0-10)</Label>
                      <Input 
                        type="number"
                        min="0"
                        max="10"
                        value={formData.pain_level} 
                        onChange={e => updateField("pain_level", parseInt(e.target.value) || 0)}
                        className="bg-slate-800 border-slate-600 text-white"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Interventions */}
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm text-white flex items-center gap-2">
                    <Shield className="w-4 h-4 text-teal-400" />
                    Interventions & Notifications
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300">Interventions Performed</Label>
                    <div className="flex flex-wrap gap-2">
                      {INTERVENTION_OPTIONS.map(int => (
                        <Badge
                          key={int.id}
                          variant={formData.interventions.includes(int.id) ? "default" : "outline"}
                          className={`cursor-pointer ${formData.interventions.includes(int.id) ? "bg-teal-600" : "border-slate-600 text-slate-300 hover:bg-slate-700"}`}
                          onClick={() => toggleArrayField("interventions", int.id)}
                        >
                          {int.name}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  {formData.interventions.includes("physician_notified") && (
                    <div className="grid grid-cols-2 gap-4 p-3 bg-slate-800 rounded-lg">
                      <div className="space-y-2">
                        <Label className="text-slate-300">Physician Name</Label>
                        <Input 
                          value={formData.physician_name} 
                          onChange={e => updateField("physician_name", e.target.value)}
                          className="bg-slate-700 border-slate-600 text-white"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-slate-300">Notification Time</Label>
                        <Input 
                          type="time"
                          value={formData.physician_notification_time} 
                          onChange={e => updateField("physician_notification_time", e.target.value)}
                          className="bg-slate-700 border-slate-600 text-white"
                        />
                      </div>
                    </div>
                  )}

                  <div className="flex items-center gap-3 p-3 bg-red-900/20 border border-red-500/30 rounded-lg">
                    <Switch 
                      checked={formData.er_transferred} 
                      onCheckedChange={c => updateField("er_transferred", c)} 
                    />
                    <Label className="text-red-300 font-medium">Patient Transferred to ER</Label>
                  </div>

                  {formData.er_transferred && (
                    <div className="space-y-2">
                      <Label className="text-slate-300">Reason for ER Transfer</Label>
                      <Textarea 
                        value={formData.er_transfer_reason} 
                        onChange={e => updateField("er_transfer_reason", e.target.value)}
                        className="bg-slate-800 border-slate-600 text-white"
                        rows={2}
                      />
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Follow-up */}
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm text-white">Follow-up Plan</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300">Fall Prevention Measures</Label>
                    <Textarea 
                      value={formData.fall_prevention_measures} 
                      onChange={e => updateField("fall_prevention_measures", e.target.value)}
                      placeholder="Document fall prevention strategies implemented..."
                      rows={2}
                      className="bg-slate-800 border-slate-600 text-white placeholder:text-slate-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Equipment Recommendations</Label>
                    <Input 
                      value={formData.equipment_recommendations} 
                      onChange={e => updateField("equipment_recommendations", e.target.value)}
                      placeholder="e.g., Walker, bed rails, hip protectors..."
                      className="bg-slate-800 border-slate-600 text-white placeholder:text-slate-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Additional Notes</Label>
                    <Textarea 
                      value={formData.notes} 
                      onChange={e => updateField("notes", e.target.value)}
                      placeholder="Additional observations or clinical notes..."
                      rows={3}
                      className="bg-slate-800 border-slate-600 text-white placeholder:text-slate-500"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Save Button */}
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={onClose} className="border-slate-600 text-slate-300">
                  Cancel
                </Button>
                <Button 
                  onClick={handleSave} 
                  disabled={saving}
                  className="bg-amber-600 hover:bg-amber-700"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Fall Event
                    </>
                  )}
                </Button>
              </div>
            </div>
          </ScrollArea>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="mt-4">
          {loadingHistory ? (
            <div className="text-center py-8 text-slate-400">
              <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
              Loading fall history...
            </div>
          ) : fallHistory.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <AlertTriangle className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>No fall events documented for this patient</p>
              <Button 
                variant="outline" 
                className="mt-4 border-slate-600"
                onClick={() => setActiveTab("new")}
              >
                Document First Fall Event
              </Button>
            </div>
          ) : (
            <ScrollArea className="h-[500px]">
              <div className="space-y-3 pr-4">
                {fallHistory.map((event, index) => (
                  <Card key={event.id || index} className="bg-slate-800/50 border-slate-700">
                    <CardContent className="pt-4">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-white">
                              {new Date(event.fall_date).toLocaleDateString()}
                            </span>
                            {event.fall_time && (
                              <span className="text-slate-400 text-sm">at {event.fall_time}</span>
                            )}
                            <Badge variant="outline" className={getSeverityColor(event)}>
                              {event.er_transferred ? "ER Transfer" : event.injuries_present ? "Injury" : "No Injury"}
                            </Badge>
                          </div>
                          <p className="text-sm text-slate-400 mt-1">
                            {FALL_LOCATIONS.find(l => l.id === event.location)?.name || event.location}
                            {event.witnessed ? " (Witnessed)" : " (Unwitnessed)"}
                          </p>
                        </div>
                      </div>

                      {event.injuries_present && event.injury_types?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-2">
                          {event.injury_types.map(type => (
                            <Badge key={type} variant="destructive" className="text-xs">
                              {INJURY_TYPES.find(t => t.id === type)?.name || type}
                            </Badge>
                          ))}
                        </div>
                      )}

                      {event.interventions?.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {event.interventions.map(int => (
                            <Badge key={int} variant="outline" className="text-xs border-slate-600 text-slate-400">
                              {INTERVENTION_OPTIONS.find(i => i.id === int)?.name || int}
                            </Badge>
                          ))}
                        </div>
                      )}

                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-700">
                        <span className="text-xs text-slate-500">
                          Documented {new Date(event.created_at).toLocaleString()}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </ScrollArea>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default FallEventOverlay;
